import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import "../setting.css";
import { useAuth } from '../../../../login/AuthProvider';

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

/* ============================================================
   AUTH HEADER HELPER
   The PIN endpoints require Authorization: Bearer <token>.
   Token lives in localStorage under `accessToken` (per app).
   Falls back to other common keys.
============================================================ */
const buildAuthHeaders = () => {
  const token =
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('idToken');

  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const PinSetup = ({ userLabId }) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [step, setStep] = useState('setup');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasPin, setHasPin] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // ⚠️ RACE CONDITION FIX: wait for AuthProvider to finish its /verify
  // before we fire our own protected requests. Without this, our
  // Authorization header may be built AFTER AuthProvider clears the
  // token from localStorage, causing a spurious 401.
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Guard against setState after unmount (prevents React warnings).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ---- Check PIN status on mount / when userLabId or auth state changes ----
  useEffect(() => {
    let cancelled = false;

    const checkPin = async () => {
      if (!userLabId) {
        if (!cancelled) setLoading(false);
        return;
      }

      // Ensure we have a token before hitting the endpoint.
      const headers = buildAuthHeaders();
      if (!headers.Authorization) {
        if (!cancelled) {
          setError('Your session has expired. Please log in again to manage your PIN.');
          setLoading(false);
        }
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/pin/lab/${userLabId}/pin-status`,
          {
            withCredentials: true,
            headers,
          }
        );
        if (cancelled || !isMountedRef.current) return;
        setHasPin(response.data.hasPin);
        if (response.data.hasPin) {
          setStep('verify');
        }
        setError('');
      } catch (err) {
        if (cancelled || !isMountedRef.current) return;
        const status = err.response?.status;
        const message =
          err.response?.data?.message || err.message || 'Unknown error';

        if (status === 401 || /access token required/i.test(message)) {
          // Token missing or expired — clear auth and prompt re-login.
          ['accessToken', 'authToken', 'token', 'jwt', 'idToken', 'refreshToken'].forEach(
            (k) => localStorage.removeItem(k)
          );
          setError('Your session has expired. Please log in again to manage your PIN.');
        } else {
          setError('Error checking PIN status: ' + message);
        }
      } finally {
        if (!cancelled && isMountedRef.current) setLoading(false);
      }
    };

    // ⚠️ RACE CONDITION FIX: don't fire until AuthProvider has settled.
    if (authLoading) {
      return () => {
        cancelled = true;
      };
    }

    if (!userLabId) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (!isAuthenticated) {
      setError('Your session has expired. Please log in again.');
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    checkPin();

    return () => {
      cancelled = true;
    };
  }, [userLabId, authLoading, isAuthenticated]);

  const handlePinChange = (index, value, setter, currentArray) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newPin = [...currentArray];
      newPin[index] = value;
      setter(newPin);
      if (value && index < 3) {
        const nextInput = document.getElementById(
          `${setter === setPin ? 'pin' : 'confirm'}-${index + 1}`
        );
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index, e, setter, currentArray) => {
    if (e.key === 'Backspace' && !currentArray[index] && index > 0) {
      const prevInput = document.getElementById(
        `${setter === setPin ? 'pin' : 'confirm'}-${index - 1}`
      );
      if (prevInput) prevInput.focus();
    }
  };

  const handleSetupSubmit = () => {
    const pinString = pin.join('');
    if (pinString.length < 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }
    setStep('confirm');
    setError('');
  };

  const handleConfirmSubmit = async () => {
    const pinString = pin.join('');
    const confirmString = confirmPin.join('');
    if (pinString !== confirmString) {
      setError('PINs do not match');
      return;
    }

    const headers = buildAuthHeaders();
    if (!headers.Authorization) {
      setError('Your session has expired. Please log in again to set your PIN.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${API_BASE_URL}/api/pin/lab/${userLabId}/pin`,
        { pin: pinString },
        {
          withCredentials: true,
          headers,
        }
      );

      if (!isMountedRef.current) return;
      setSuccess('PIN set successfully!');

      sessionStorage.setItem('pinVerified', 'true');
      sessionStorage.setItem('pinVerifiedForLab', userLabId);
      sessionStorage.setItem('pinVerifiedTimestamp', Date.now().toString());

      // Delay navigation, but guard against unmount.
      setTimeout(() => {
        if (isMountedRef.current) {
          navigate(`/statement-email/lab/${userLabId}`);
        }
      }, 2000);
    } catch (err) {
      if (!isMountedRef.current) return;
      const status = err.response?.status;
      const message =
        err.response?.data?.message || err.message || 'Error setting PIN';
      if (status === 401 || /access token required/i.test(message)) {
        setError('Your session has expired. Please log in again to set your PIN.');
      } else {
        setError(message);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleVerifySubmit = async () => {
    const pinString = pin.join('');

    if (pinString.length < 4) {
      setError('Please enter your 4-digit PIN');
      return;
    }

    const headers = buildAuthHeaders();
    if (!headers.Authorization) {
      setError('Your session has expired. Please log in again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/pin/lab/${userLabId}/verify-pin`,
        { pin: pinString },
        {
          withCredentials: true,
          headers,
        }
      );

      if (!isMountedRef.current) return;

      if (response.data.success) {
        setSuccess('PIN verified successfully!');

        sessionStorage.setItem('pinVerified', 'true');
        sessionStorage.setItem('pinVerifiedForLab', userLabId);
        sessionStorage.setItem('pinVerifiedTimestamp', Date.now().toString());

        document.cookie = `pinVerified=${userLabId}; path=/; max-age=3600`;

        setTimeout(() => {
          if (isMountedRef.current) {
            navigate(`/statement-email/lab/${userLabId}`);
          }
        }, 1500);
      } else {
        setError('Invalid PIN');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      const status = err.response?.status;
      const message =
        err.response?.data?.message || err.message || 'Invalid PIN';
      if (status === 401 || /access token required/i.test(message)) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(message);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const renderPinInputs = (pinArray, setter, prefix) => {
    return (
      <div className="pin-inputs-row">
        {[0, 1, 2, 3].map((index) => (
          <input
            key={index}
            id={`${prefix}-${index}`}
            type="password"
            maxLength="1"
            value={pinArray[index]}
            onChange={(e) => handlePinChange(index, e.target.value, setter, pinArray)}
            onKeyDown={(e) => handleKeyDown(index, e, setter, pinArray)}
            disabled={loading}
            className="pin-input"
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pin-wrapper">
        <div className="pin-card">
          <h3 className="pin-title">Loading PIN Settings...</h3>
          <div>Please wait...</div>
        </div>
      </div>
    );
  }

  if (!userLabId) {
    return (
      <div className="pin-wrapper">
        <div className="pin-card">
          <h3 className="pin-title-error">Error</h3>
          <div>Invalid lab ID. Please try again.</div>
          <button onClick={() => navigate('/Tabs')} className="pin-back-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pin-wrapper">
      <div className="pin-card">
        <h3 className="pin-title">
          {step === 'setup' && 'Set Up PIN'}
          {step === 'confirm' && 'Confirm PIN'}
          {step === 'verify' && 'Enter PIN'}
        </h3>

        {error && <div className="pin-error-box">{error}</div>}
        {success && <div className="pin-success-box">{success}</div>}

        <div>
          {step === 'setup' && renderPinInputs(pin, setPin, 'pin')}
          {step === 'confirm' && renderPinInputs(confirmPin, setConfirmPin, 'confirm')}
          {step === 'verify' && renderPinInputs(pin, setPin, 'pin')}
        </div>

        <div className="pin-btn-wrapper">
          <button
            onClick={() => {
              if (step === 'setup') handleSetupSubmit();
              if (step === 'confirm') handleConfirmSubmit();
              if (step === 'verify') handleVerifySubmit();
            }}
            disabled={loading}
            className="pin-btn"
          >
            {loading
              ? 'Please wait...'
              : step === 'setup'
              ? 'Next'
              : step === 'confirm'
              ? 'Confirm'
              : 'Verify'}
          </button>
        </div>

        {step === 'verify' && (
          <p className="pin-hint">
            Enter your 4-digit PIN to access statement email settings
          </p>
        )}

        {step === 'setup' && (
          <p className="pin-hint">
            Set up a 4-digit PIN to secure your statement email settings
          </p>
        )}
      </div>
    </div>
  );
};

export default PinSetup;